import type { JSX, ReactNode } from 'react';
import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { TableCell, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import type { Experience } from '../types/experience.types';
import { formatDateInfo, calculateDuration } from '../types/experience.types';

interface ExperienceExpandableRowProps {
  experience: Experience;
  expandedContent: ReactNode;
  statusBadge: ReactNode;
  actions: ReactNode;
}

function CompanyNameTrigger({ companyName }: { companyName: string }): JSX.Element {
  return (
    <CollapsibleTrigger asChild>
      <button type="button" className="font-medium text-left hover:underline cursor-pointer">
        {companyName}
      </button>
    </CollapsibleTrigger>
  );
}

function ExpandToggleButton({ isExpanded }: { isExpanded: boolean }): JSX.Element {
  return (
    <CollapsibleTrigger asChild>
      <Button variant="ghost" size="icon" className="h-6 w-6" aria-label="Toggle verifications">
        {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </Button>
    </CollapsibleTrigger>
  );
}

export function ExperienceExpandableRow({
  experience,
  expandedContent,
  statusBadge,
  actions,
}: ExperienceExpandableRowProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded} asChild>
      <>
        <TableRow className="group">
          <TableCell className="w-[40px] p-2">
            <ExpandToggleButton isExpanded={isExpanded} />
          </TableCell>
          <TableCell className="font-medium">
            <CompanyNameTrigger companyName={experience.companyName} />
          </TableCell>
          <TableCell>{experience.jobTitle}</TableCell>
          <TableCell>
            {formatDateInfo(experience.startDate)} -{' '}
            {experience.isOngoing ? 'Present' : formatDateInfo(experience.endDate)}
          </TableCell>
          <TableCell>
            {calculateDuration(experience.startDate, experience.endDate, experience.isOngoing)}
          </TableCell>
          <TableCell>{statusBadge}</TableCell>
          <TableCell className="text-right">{actions}</TableCell>
        </TableRow>
        <CollapsibleContent asChild>
          <tr>
            <td colSpan={7} className="p-0">
              {expandedContent}
            </td>
          </tr>
        </CollapsibleContent>
      </>
    </Collapsible>
  );
}
